import * as Sentry from '@sentry/browser';
import { useMutation, useQueryClient } from 'react-query';
import { ace_api } from '@axo/shared/data-access/api';
import { useAPI } from '@axo/shared/data-access/provider';
import { ace } from '@axo/shared/data-access/types';
import { callsKeys } from '../../cache-keys/callsKeys';
import { sanitizeForSentry } from './sanitizeForSentry';

export function useCreateCallOrder() {
  const client = useQueryClient();
  const api = useAPI();

  return useMutation({
    mutationFn: ace_api.createCallOrder(api),
    onSuccess: (data: ace.CallOrder | undefined, variables) => {
      // `send` returns `undefined` on HTTP 429 (rate-limit) — see send.ts.
      // Treat it as a non-fatal "request didn't go through" signal and bail
      // before destructuring, so we don't throw in onSuccess and bury the
      // failure inside react-query's internal logger.
      if (!data) {
        Sentry.captureMessage(
          'createCallOrder resolved with undefined (likely 429)',
          {
            level: 'warning',
            extra: { variables: sanitizeForSentry(variables) },
          }
        );
        return;
      }

      return Promise.all([
        client.invalidateQueries([
          callsKeys.records({ LoanApplicationID: data.LoanApplicationID }),
        ]),
        client.invalidateQueries([
          callsKeys.orders({ LoanApplicationID: data.LoanApplicationID }),
        ]),
      ]);
    },
    onError: (error, variables) => {
      Sentry.captureException(
        error instanceof Error ? error : new Error(String(error)),
        {
          extra: {
            mutation: 'createCallOrder',
            variables: sanitizeForSentry(variables),
          },
        }
      );
    },
  });
}
